import React, { useState } from "react";

interface calProps {
  amortization: number;
  payment: number;
  interestRate: number;
  loanAmount: number;
  term: number;
  payFreq: number;
  text?: string;
}

const PaymentBreakdown = ({
  amortization,
  payment,
  interestRate,
  loanAmount,
  term,
  payFreq,
}: calProps) => {
  const totalRows = Array.from(
    { length: amortization },
    (_, index) => index + 1
  );
  const currentDate = new Date();
  const lastYear = currentDate.getFullYear() - 1;
  let startLoanAmount = loanAmount;
  let outerBalanceAmount = loanAmount;

  const [collapsedRows, setCollapsedRows] = useState<any>({});

  const toggleCollapse = (index: number) => {
    setCollapsedRows((prev: any) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  let termPrincipalPaid = 0;
  let termInterestPaid = 0;

  return (
    <div>
      <h5 className="mb-2 text-xl font-bold blueColor">Payments Breakdown</h5>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left rtl:text-right text-gray-500">
          <thead className="text-xs font-light text-gray-700 uppercase bg-black-50 border-b">
            <tr>
              <th className="px-6 py-4">Year</th>
              <th className="px-6 py-4">Total Payment</th>
              <th className="px-6 py-4">Principal Paid</th>
              <th className="px-6 py-4">Interest Paid</th>
              <th className="px-6 py-4">Balance</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {totalRows.map((row, index) => {
              const annuallyIntRate = interestRate / 200;
              const effectiveAnnualIntRate =
                Math.pow(1 + annuallyIntRate, 2) - 1;

              let intPaid = 0;
              let principalPaid = 0;

              {
                // eslint-disable-next-line @typescript-eslint/no-extra-semi
                [...Array(Number(payFreq))].map((_, i) => {
                  const monthlyIntRate =
                    Math.pow(1 + effectiveAnnualIntRate, 1 / Number(payFreq)) -
                    1;
                  const innerIntPaid = outerBalanceAmount * monthlyIntRate;
                  intPaid += innerIntPaid;

                  const innerPrincipalPaid = payment - innerIntPaid;
                  principalPaid += innerPrincipalPaid;

                  let innerBalance = outerBalanceAmount - innerPrincipalPaid;
                  if (innerBalance < 0 && Math.abs(innerBalance) < 0.01) {
                    innerBalance = 0;
                  }
                  outerBalanceAmount = innerBalance;
                });
              }

              termPrincipalPaid += principalPaid;
              termInterestPaid += intPaid;

              return (
                <React.Fragment key={index}>
                  <tr className="bg-white border-b">
                    <th
                      scope="row"
                      className="px-6 py-4 font-normal text-gray-700 whitespace-nowrap"
                    >
                      {lastYear + row}
                    </th>
                    <td className="px-6 py-4 font-normal text-gray-700">
                      ${(payment * payFreq).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 font-normal text-gray-700">
                      ${principalPaid.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 font-normal text-gray-700">
                      ${intPaid.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 font-normal text-gray-700">
                      ${outerBalanceAmount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-normal">
                      <a
                        href="javascript:void(0)"
                        onClick={() => toggleCollapse(index)}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        <svg
                          className=""
                          focusable="false"
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                        >
                          <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                        </svg>
                      </a>
                    </td>
                  </tr>
                  <tr
                    className={`bg-white border-b ${
                      collapsedRows[index] ? "" : "hidden"
                    }`}
                    id={`collapse${index}`}
                  >
                    <td colSpan={6} className="p-0">
                      <div className="px-6 py-4">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                          <thead className="text-xs font-light text-gray-700 uppercase bg-black-50">
                            <tr className="border-b bg-white">
                              <th className="px-4 py-2 font-normal text-gray-700">
                                Total
                              </th>
                              <th className="px-4 py-2 font-normal text-gray-700">
                                Principal
                              </th>
                              <th className="px-4 py-2 font-normal text-gray-700">
                                Interest
                              </th>
                              <th className="px-4 py-2 font-normal text-gray-700">
                                Balance
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...Array(Number(payFreq))].map((_, i) => {
                              const monthlyIntRate =
                                Math.pow(
                                  1 + effectiveAnnualIntRate,
                                  1 / Number(payFreq)
                                ) - 1;
                              const innerIntPaid =
                                startLoanAmount * monthlyIntRate;
                              const innerPrincipalPaid = payment - innerIntPaid;
                              let innerBalance =
                                startLoanAmount - innerPrincipalPaid;
                              if (
                                innerBalance < 0 &&
                                Math.abs(innerBalance) < 0.01
                              ) {
                                innerBalance = 0;
                              }
                              // Update loanAmount for the next iteration
                              startLoanAmount = innerBalance;
                              return (
                                <tr key={i}>
                                  <td className="border-b px-4 py-2 font-normal text-gray-700">
                                    ${payment.toFixed(2)}
                                  </td>
                                  <td className="border-b px-4 py-2 font-normal text-gray-700">
                                    ${innerPrincipalPaid.toFixed(2)}
                                  </td>
                                  <td className="border-b px-4 py-2 font-normal text-gray-700">
                                    ${innerIntPaid.toFixed(2)}
                                  </td>
                                  <td className="border-b px-4 py-2 font-normal text-gray-700">
                                    ${innerBalance.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                  {index + 1 === Number(term) && (
                    <>
                      <tr className="bg-white border-b">
                        <th
                          scope="row"
                          className="px-6 py-4 font-bold text-gray-700 whitespace-nowrap"
                        >
                          Term Total
                        </th>
                        <td className="px-6 py-4 font-bold text-gray-700">
                          ${(payment * (payFreq * term)).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-700">
                          ${termPrincipalPaid.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-700">
                          ${termInterestPaid.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-700">
                          ${outerBalanceAmount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 font-normal text-gray-700"></td>
                      </tr>
                      <tr className="bg-white border-b">
                        <td
                          className="px-6 py-4 font-normal text-gray-700 text-center bg-blue-50"
                          colSpan={6}
                        >
                          The line above displays the totals at the end of your
                          mortgage term. At this time, you will renew your
                          mortgage and choose among the rates that are
                          available. The following analysis assumes you will
                          lock in the same rate for the remainder of the
                          amortization period, which may not be possible.
                        </td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentBreakdown;
